/**
 * Tap versus long press on a Roll button (docs/design.md: tap to roll, long-press to enter a
 * physical die). Pointer events start a timer; a press held for LONG_PRESS_MS fires the long
 * action and swallows the click that follows; a shorter press lets the click fire the tap.
 * Keyboard activation arrives as a click alone and taps.
 */
export const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE = 12;

export interface LongPressHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
  onPointerLeave: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onClick: (e: MouseEvent) => void;
  onContextMenu: (e: MouseEvent) => void;
}

/**
 * Handlers for one element (or a container that delegates): `target` picks the element a press
 * belongs to (null ignores the press), `onTap` and `onLong` receive it.
 */
export function longPress<T extends Element>(target: (e: Event) => T | null, onTap: (el: T) => void, onLong: (el: T) => void, delay = LONG_PRESS_MS): LongPressHandlers {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pressed: T | null = null;
  let fired = false;
  let origin = { x: 0, y: 0 };
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return {
    onPointerDown: (e) => {
      const el = target(e);
      if (!el || (e.button !== undefined && e.button !== 0)) return;
      clear();
      pressed = el;
      fired = false;
      origin = { x: e.clientX, y: e.clientY };
      timer = setTimeout(() => {
        timer = null;
        fired = true;
        onLong(el);
      }, delay);
    },
    onPointerUp: () => {
      clear();
    },
    onPointerCancel: () => {
      clear();
      pressed = null;
    },
    onPointerLeave: () => {
      clear();
      pressed = null;
    },
    onPointerMove: (e) => {
      if (timer === null) return;
      if (Math.abs(e.clientX - origin.x) > MOVE_TOLERANCE || Math.abs(e.clientY - origin.y) > MOVE_TOLERANCE) {
        clear();
        pressed = null;
      }
    },
    onClick: (e) => {
      const el = target(e);
      if (!el) return;
      if (fired && el === pressed) {
        // The long press already acted; the click that ends the press is not a tap.
        fired = false;
        pressed = null;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      pressed = null;
      onTap(el);
    },
    onContextMenu: (e) => {
      // A held press on iOS Safari and on desktop would open a menu: the long press is ours.
      if (target(e)) e.preventDefault();
    },
  };
}
